<template>
  <div v-if="hasReplies">
    <div class="flex">
      <div class="w-10 min-w-[40px] flex justify-center">
        <div
          class="bg-neutral-300 dark:bg-neutral-700 w-[2px] lg:w-[4px] h-full"
        ></div>
      </div>
      <NamiLoading v-if="isFetchingNextPage" size="small" />
      <NamiButton
        v-else-if="hasNextPage"
        buttonType="primary"
        text
        block
        small
        @buttonClick="fetchNextPage"
      >
        Load earlier replies
      </NamiButton>
    </div>
    <div class="space-y-1 mt-1">
      <FeedCommentCard
        v-for="comment in data ?? []"
        :key="comment.id"
        :comment="comment"
        :feed-type
        :feed-id
        :parent-id="parentComment.id"
        :highlightAuthorIds
        :highlightAuthorTag
        show-reply-line
        no-replies
      >
        <template #composer="composerAttrs">
          <slot name="composer" v-bind="composerAttrs"></slot>
        </template>
        <template #options>
          <!-- Comment actions -->
        </template>
      </FeedCommentCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Comment,
  type CommentableResource,
  type CommentEntity,
  isComment,
} from "~/src/api";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import {
  getCachedInfiniteCommentQuery,
  useCachedInfiniteCommentQuery,
} from "~/utils/tanstack/comments";

interface Props {
  parentComment: CommentEntity;
  feedType: CommentableResource;
  feedId: string;

  highlightAuthorIds?: string[];
  highlightAuthorTag?: string;

  hideInput?: boolean;
  disableReplies?: boolean;
}

const { parentComment, feedType, feedId } = defineProps<Props>();

const queryClient = useQueryClient();
const queryKey = ["comments", "replies", computed(() => parentComment.id)];
const { data: replyQueryCache } = getCachedInfiniteCommentQuery(
  queryClient,
  queryKey,
);

const hasReplies = computed(() => {
  const queryTotal = replyQueryCache.value?.pages[0]?.meta.total ?? 0;
  if (queryTotal > 0) return true;
  return parentComment.relationships.filter(isComment).length > 0;
});

const emit = defineEmits<{
  (e: "commentPost"): void;
  (e: "commentDelete"): void;
  (e: "noReplies"): void;
}>();

const { data, fetchNextPage, isFetchingNextPage, hasNextPage, suspense } =
  useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) =>
      Comment.search(feedType, feedId, {
        parentId: parentComment.id,
        offset: pageParam,
        limit: 3,
        order: {
          createdAt: "desc",
        },
        includes: ["user", "profile_frame", "organization", "badge"],
        // showDeleted: false,
      }),
    initialPageParam: 0,
    getNextPageParam: (prev) => {
      if (prev && prev.meta.offset + prev.meta.limit >= prev.meta.total)
        return null;
      return prev.meta.offset + prev.meta.limit;
    },
    select: (data) =>
      data.pages.toReversed().flatMap((page) => page.data.toReversed()),
    enabled: hasReplies,
    staleTime: 1000 * 60 * 5,
  });

await suspense();

watch(
  data,
  (value) => {
    if (!value) return;
    if (!hasNextPage.value && value.length === 0) emit("noReplies");
  },
  { immediate: true },
);
</script>

<style scoped lang="postcss"></style>
